import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

export type ArticleCtaVariant = 'trial' | 'contact' | 'markets';

export interface PulseArticle {
  slug: string;
  /** ISO 8601 date (YYYY-MM-DD) — used for sorting and JSON-LD `datePublished`. */
  date: string;
  /** Display-friendly date (e.g. "Apr 15, 2026"). */
  displayDate: string;
  title: string;
  /** Short summary used on the index card and as the meta description. */
  excerpt: string;
  /** SEO keywords. */
  keywords: string[];
  /** Optional override for the CTA shown at the bottom of the article. */
  ctaVariant?: ArticleCtaVariant;
  /** Optional pointer to an in-platform market deep-dive used by the `markets` CTA. */
  ctaMarketsHref?: string;
  /** Lazy-loaded body component (one default export per file). */
  body: LazyExoticComponent<ComponentType<{}>>;
}

export const pulseArticles: PulseArticle[] = [
  {
    slug: 'biomarin-roctavian-gene-therapy-economics',
    date: '2026-09-30',
    displayDate: 'Sep 30, 2026',
    title: 'When Approved Therapies Fail: BioMarin\'s Roctavian Retreat and the Economics of Gene Therapy',
    excerpt:
      'BioMarin withdrew FDA-approved Roctavian in February 2026 after $36 million in 2025 sales and no qualified buyer—a commercial and capital-allocation case study, not a verdict on gene therapy as a modality.',
    keywords: [
      'Roctavian',
      'BioMarin',
      'gene therapy',
      'hemophilia A',
      'AAV',
      'Amicus Therapeutics',
      'outcomes-based agreements',
      'cell and gene therapy',
      'rare disease',
      'commercial withdrawal',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./biomarin-roctavian-gene-therapy-economics')),
  },
  {
    slug: 'cgt-pipeline-sns-tracks',
    date: '2026-08-21',
    displayDate: 'Aug 21, 2026',
    title: 'The Cell and Gene Therapy Pipeline: What SNS Tracks and What\'s Beyond',
    excerpt:
      'SNS tracks 243 CGT candidates with realistic approval paths—roughly 20–25% of global activity—while non-oncology now represents 51% of the pipeline and manufacturing constraints continue to shape in vivo gene therapy development.',
    keywords: [
      'cell and gene therapy',
      'CGT pipeline',
      'CAR-T',
      'gene-modified cell therapy',
      'in vivo gene therapy',
      'RNA therapeutics',
      'non-oncology',
      'allogeneic CAR-T',
      'SNS Research',
      'clinical holds',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./cgt-pipeline-sns-tracks')),
  },
  {
    slug: 'gene-modified-cell-therapy-cgt',
    date: '2026-08-13',
    displayDate: 'Aug 13, 2026',
    title: 'Gene-Modified Cell Therapy: Dominant Modality Within CGT',
    excerpt:
      'GMCT—primarily CAR-T—is projected to reach $20–55B by 2036, but growth remains concentrated in hematologic malignancies while autoimmune and solid-tumor expansion, payer cost-of-care pressure, and centralized manufacturing constraints shape the strategic landscape.',
    keywords: [
      'gene-modified cell therapy',
      'CAR-T',
      'cell and gene therapy',
      'Yescarta',
      'Kymriah',
      'allogeneic CAR-T',
      'point-of-care manufacturing',
      'hematologic malignancies',
      'autoimmune disease',
      'solid tumors',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./gene-modified-cell-therapy-cgt')),
  },
  {
    slug: 'inherited-retinal-diseases-gene-therapy',
    date: '2026-08-11',
    displayDate: 'Aug 11, 2026',
    title: 'Inherited Retinal Diseases: Gene Therapy Market Emerging, Clinical Pipeline Narrowing',
    excerpt:
      'Luxturna remains the sole approved IRD gene therapy with modest real-world outcomes; Biogen Phase 3 failures and Roche’s Spark write-down have narrowed the pipeline and cooled large-pharma IRD M&A.',
    keywords: [
      'inherited retinal disease',
      'gene therapy',
      'Luxturna',
      'RPE65',
      'Spark Therapeutics',
      'Biogen',
      'Regenxbio',
      'Atsena Therapeutics',
      '4D Molecular Therapeutics',
      'ophthalmology',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./inherited-retinal-diseases-gene-therapy')),
  },
  {
    slug: 'beyond-anti-vegf-ophthalmic-innovation',
    date: '2026-07-30',
    displayDate: 'Jul 30, 2026',
    title: 'Beyond Anti-VEGF: The Next Generation of Ophthalmic Innovation',
    excerpt:
      'Anti-VEGF innovation is flattening into line-extensions while complement inhibitors, semaphorin modulators, and immunomodulatory agents emerge as the next growth cycle in geographic atrophy, diabetic retinopathy, and immune-mediated retinal disease.',
    keywords: [
      'anti-VEGF',
      'ophthalmology',
      'complement inhibitors',
      'geographic atrophy',
      'diabetic retinopathy',
      'immune modulation',
      'semaphorin inhibitors',
      'wet AMD',
      'mechanism diversification',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./beyond-anti-vegf-ophthalmic-innovation')),
  },
  {
    slug: 'injection-economy-ophthalmics',
    date: '2026-07-28',
    displayDate: 'Jul 28, 2026',
    title: 'The Injection Economy',
    excerpt:
      'Intravitreal therapies growing 7.9% vs. topical 3.5%. Sustained-release implants accelerating at 9.1% CAGR. Distribution shifting toward hospital/specialty pharmacy—with strategic implications through 2036.',
    keywords: [
      'intravitreal',
      'ophthalmology',
      'sustained-release implants',
      'route of administration',
      'specialty pharmacy',
      'retinal disease',
      'drug delivery',
      'hospital systems',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./injection-economy-ophthalmics')),
  },
  {
    slug: 'cell-gene-therapy-retinal-disease',
    date: '2026-05-12',
    displayDate: 'May 12, 2026',
    title: 'Cell and Gene Therapy in Retinal Disease',
    excerpt:
      'Ophthalmology leads CGT development: AAV gene replacement, optogenetics, stem-cell RPE repair, and ASOs are reshaping inherited retinal disease, wet AMD, and geographic atrophy—with pipeline, manufacturing, and endpoint challenges still to resolve.',
    keywords: [
      'cell and gene therapy',
      'retinal disease',
      'AAV',
      'Luxturna',
      'optogenetics',
      'MCO-010',
      'GS030',
      'geographic atrophy',
      'inherited retinal disease',
      'ophthalmology',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./cell-gene-therapy-retinal-disease')),
  },
  {
    slug: 'oral-biologics-needle-barrier',
    date: '2026-04-15',
    displayDate: 'Apr 15, 2026',
    title: 'The Revolution of Oral Biologics: Breaking the Needle Barrier',
    excerpt:
      'Q2 2026 dermatology pipelines reveal a decisive shift toward oral biologic delivery. Icotyde and the RaniPill platform are dissolving the needle barrier that has defined the biologic era for two decades.',
    keywords: [
      'oral biologics',
      'Icotyde',
      'JNJ-2113',
      'IL-23 receptor',
      'RaniPill',
      'ustekinumab biosimilar',
      'plaque psoriasis',
      'dermatology pipeline',
    ],
    ctaVariant: 'markets',
    ctaMarketsHref: '/markets/dermatology',
    body: lazy(() => import('./oral-biologics-needle-barrier')),
  },
  {
    slug: 'jak-tyk2-inhibitors-dermatology',
    date: '2026-03-26',
    displayDate: 'Mar 26, 2026',
    title:
      'Strategic Expansion of JAK and TYK2 Inhibitors in Dermatology: Beyond Psoriasis',
    excerpt:
      'Selective TYK2 and JAK1 inhibitors are pivoting from plaque psoriasis to high-unmet-need indications such as alopecia areata, hidradenitis suppurativa, vitiligo, and cutaneous lupus.',
    keywords: [
      'JAK inhibitors',
      'TYK2 inhibitors',
      'deucravacitinib',
      'zasocitinib',
      'povorcitinib',
      'alopecia areata',
      'hidradenitis suppurativa',
      'vitiligo',
      'cutaneous lupus',
    ],
    ctaVariant: 'markets',
    ctaMarketsHref: '/markets/dermatology',
    body: lazy(() => import('./jak-tyk2-inhibitors-dermatology')),
  },
  {
    slug: 'dermatology-key-players-2026',
    date: '2026-02-12',
    displayDate: 'Feb 12, 2026',
    title:
      'Key Players in Dermatology: Leadership Dynamics and Market Share Projections for 2026',
    excerpt:
      'AbbVie, J&J, Pfizer, and Novartis still anchor the dermatology leaderboard, but Eli Lilly, Galderma, and LEO Pharma are reshaping share through differentiated mechanisms and niche focus.',
    keywords: [
      'dermatology market',
      'AbbVie',
      'Johnson & Johnson',
      'Pfizer',
      'Novartis',
      'Eli Lilly',
      'Galderma',
      'LEO Pharma',
      'biosimilar',
      '2026 forecast',
    ],
    ctaVariant: 'markets',
    ctaMarketsHref: '/markets/dermatology',
    body: lazy(() => import('./dermatology-key-players-2026')),
  },
  {
    slug: 'drugs-to-watch-2026',
    date: '2026-01-27',
    displayDate: 'Jan 27, 2026',
    title:
      'Drugs to Watch in 2026: Breakthroughs in Targeted Therapies and Gene Modification',
    excerpt:
      'Late-stage and emerging assets across selective kinase inhibitors, oral cytokine modulators, TIL cellular therapy, and gene-based platforms are redefining dermatology and immuno-oncology.',
    keywords: [
      'drugs to watch 2026',
      'zasocitinib',
      'icotrokinra',
      'lifileucel',
      'TIL therapy',
      'gene therapy',
      'pz-cel',
      'mRNA vaccine',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./drugs-to-watch-2026')),
  },
  {
    slug: 'il23-il17-axis-2026',
    date: '2026-01-12',
    displayDate: 'Jan 12, 2026',
    title:
      'Navigating the IL-23/IL-17 Axis: Resilience and Differentiation in a Biosimilar-Disrupted 2026 Landscape',
    excerpt:
      'Despite biosimilar disruption from multiple ustekinumab launches, the IL-23/IL-17 axis remains the cornerstone for moderate-to-severe psoriasis. Differentiation now hinges on oral peptides, dual inhibition, and ecosystem moats.',
    keywords: [
      'IL-23',
      'IL-17',
      'psoriasis',
      'biosimilar',
      'Stelara',
      'Skyrizi',
      'Tremfya',
      'icotrokinra',
      'bimekizumab',
      'IRA',
    ],
    ctaVariant: 'trial',
    body: lazy(() => import('./il23-il17-axis-2026')),
  },
];

/**
 * Return articles sorted newest-first by ISO `date`.
 * Memoised once at import time — registry is static.
 */
export const pulseArticlesSorted: PulseArticle[] = [...pulseArticles].sort(
  (a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0),
);

export const getPulseArticleBySlug = (
  slug: string,
): PulseArticle | undefined =>
  pulseArticles.find((article) => article.slug === slug);
